import { Injectable, inject } from '@angular/core';
import { ENUM_ActivityEventType } from '@api-new/common';
import { ComponentStore, OnStoreInit } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, take } from 'rxjs';
import { tap } from 'rxjs/operators';
import { logAnalyticsEvent } from './analytics/analytics.actions';

export interface CommonDialogState {
  isDialogPositionUp: boolean;
}
@Injectable()
export abstract class CommonDialogStore<StoreModel extends CommonDialogState, CloseDialogModel = void>
  extends ComponentStore<StoreModel>
  implements OnStoreInit
{
  protected readonly dialogRef = inject(DynamicDialogRef);
  protected readonly globalStore = inject(Store);

  protected analyticParams: { eventName: string; closeDialogModelAttribute?: keyof CloseDialogModel };

  // eslint-disable-next-line @angular-eslint/prefer-inject
  protected constructor(initData: StoreModel, analyticParams: { eventName: string; closeDialogModelAttribute?: keyof CloseDialogModel }) {
    super(initData);
    this.analyticParams = analyticParams;
  }

  ngrxOnStoreInit(): void {
    this.dialogOpened();
    this.onChildStoreInit();
    this._listenToDialogClose();
  }

  protected dialogOpened(): void {
    this.globalStore.dispatch(
      logAnalyticsEvent({
        event: { type: ENUM_ActivityEventType.ACTIVITY_EVENT_TYPE_MODAL, name: this.analyticParams?.eventName + '_opened' },
      }),
    );
  }

  protected dialogClosed(event: CloseDialogModel | null | undefined): void {
    const { closeDialogModelAttribute } = this.analyticParams;
    const closeResult = closeDialogModelAttribute ? event?.[closeDialogModelAttribute] : event;

    const eventNameSuffix = closeResult ? '_accepted' : '_closed';

    this.globalStore.dispatch(
      logAnalyticsEvent({
        event: { type: ENUM_ActivityEventType.ACTIVITY_EVENT_TYPE_MODAL, name: this.analyticParams?.eventName + eventNameSuffix },
      }),
    );
  }

  // METHODS TO OVERRIDE ---------------------------------------------------------------------------------------------

  protected onChildStoreInit(): void {
    // Override
  }

  // SELECTORS ---------------------------------------------------------------------------------------------------------

  readonly isDialogPositionUp$ = this.select((state) => state.isDialogPositionUp);

  // ACTIONS ---------------------------------------------------------------------------------------------------------

  readonly closeDialog = this.effect((closeDialog$: Observable<CloseDialogModel | null>) =>
    closeDialog$.pipe(
      tap((closeDialogData) => {
        this.setIsDialogPositionUp(false);
        setTimeout(() => this.dialogRef.close(closeDialogData), 250);
      }),
    ),
  );

  readonly moveDialogUp = this.effect((runAnimation$: Observable<void>) =>
    runAnimation$.pipe(tap(() => setTimeout(() => this.setIsDialogPositionUp(true), 1))),
  );

  private readonly _listenToDialogClose = this.effect<void>((trigger$) =>
    trigger$.pipe(
      take(1),
      tap(() => this.dialogRef.onClose.pipe(take(1)).subscribe((event) => this.dialogClosed(event))),
    ),
  );

  // REDUCERS ---------------------------------------------------------------------------------------------------------

  readonly setIsDialogPositionUp = this.updater((state, isDialogPositionUp: boolean) => ({
    ...state,
    isDialogPositionUp,
  }));
}
